import { useTranslation } from 'react-i18next'

import {
  blocksCapped,
  useCreateQuoteBlock,
  useDeleteQuoteBlock,
  useQuoteBlocks,
  useUpdateQuoteBlock,
} from '@/features/quotes/api/useQuoteBlocks'
import { errorMessage } from '@/features/quotes/quoteText'
import type { QuoteBlock, QuoteBlockKind, QuoteDetail } from '@/types/domain'

/* ONE answer to how a document block is added, amended, removed and refused —
 * for both cards that show them (Story 5.2, FR-60).
 *
 * These blocks print on the offer: the inclusions as one list, the exclusions as
 * another after the money, the notes last, verbatim. Two surfaces render them —
 * `QuoteBlocksCard` carries what the price does and does not cover, and
 * `QuoteNotesCard` carries the notes — and the whole reason this hook exists is
 * that they must not become two implementations. A second copy would be a second
 * place deciding what a refusal says and what a half-typed line does, and the
 * two would drift on exactly the failure nobody exercises.
 *
 * ── One line in, one line out ───────────────────────────────────────────────
 *
 * Every write is a BODY and nothing else. There was a form sheet here with a
 * label, a body and a price note, and it was the wrong shape for the thing: a
 * block is one sentence a client reads, typed once, and a modal asking three
 * questions to add "Customs duties" is three questions too many. The lists are
 * edited in place now.
 *
 * ── Where the label comes from ──────────────────────────────────────────────
 *
 * The server requires one (block.go, and migration 0033's CHECK behind it) and
 * nobody is asked for one any more, so it is DERIVED from the body — the same
 * text, cut to the column's 120. That is honest about what the field is: 0032
 * marks a template's label A and its body S, the label never prints, and its
 * only remaining job is to be a handle in a list nobody browses. It is derived
 * here rather than defaulted on the server so the server's rule stays a rule
 * and this stays a client convenience.
 *
 * An amend keeps the two in step for the same reason, and CARRIES THE PRICE
 * NOTE FORWARD: the write is a whole-record PUT, so an exclusion edited without
 * it would silently lose the "as per tariff" beside it.
 */

/** The server's `maxBlockLabelLength`, mirrored so a long note is cut here
 *  rather than refused there. */
const MAX_LABEL = 120

function labelFor(body: string): string {
  return body.trim().slice(0, MAX_LABEL)
}

export interface BlockDesk {
  loading: boolean
  failedToLoad: boolean
  /** Past draft, and for the reason the cargo table is: a sent quote is what the
   *  client was actually shown. The server refuses the write either way
   *  (requireEditable), so a visible-but-refused control would only be a slower
   *  way to learn that — while the lists stay READABLE, because what was printed
   *  is precisely what somebody opening a sent quote came to check. */
  readOnly: boolean
  /** Whichever write last failed, already worded. Undefined is the ordinary case. */
  failureText?: string
  pending: boolean
  /** The blocks of one kind, in the order the offer prints them. */
  of: (kind: QuoteBlockKind) => QuoteBlock[]
  /** `done` runs on SUCCESS only, so a refusal leaves the typed line on screen
   *  rather than clearing it (UX-DR-18). */
  create: (kind: QuoteBlockKind, body: string, done?: () => void) => void
  amend: (block: QuoteBlock, body: string, done?: () => void) => void
  remove: (block: QuoteBlock) => void
}

/* Both cards call this, and both therefore issue `useQuoteBlocks` on the same
 * key: React Query answers the second from the first's result, so this is one
 * fetch and not two. The mutations are per-desk, which is what keeps a refusal
 * in one card from lighting up an alert in the other. */
export function useBlockDesk(record: QuoteDetail): BlockDesk {
  const { t } = useTranslation()

  const { data: blocks, isPending, isError } = useQuoteBlocks(record.id)
  const create = useCreateQuoteBlock(record.id)
  const update = useUpdateQuoteBlock(record.id)
  const remove = useDeleteQuoteBlock(record.id)

  const pending = create.isPending || update.isPending || remove.isPending
  // Whichever write last failed is the one to report, matching CargoSection.
  const failure = create.error ?? update.error ?? remove.error

  /* The cap refusal is worded here rather than by the generic mapping: "409
     conflict" tells a reader nothing they can act on, and the only thing that
     makes room is deleting one of the entries in front of them. */
  const failureText = failure
    ? blocksCapped(failure)
      ? t('quotes.blocks.capped')
      : errorMessage(failure, t)
    : undefined

  return {
    loading: isPending,
    failedToLoad: isError,
    readOnly: record.status !== 'draft',
    failureText,
    pending,
    of: (kind) => (blocks ?? []).filter((b) => b.kind === kind),
    create: (kind, body, done) =>
      create.mutate(
        { kind, label: labelFor(body), body: body.trim() },
        { onSuccess: done },
      ),
    amend: (block, body, done) =>
      update.mutate(
        {
          blockId: block.id,
          payload: {
            label: labelFor(body),
            body: body.trim(),
            /* Only an exclusion may carry one — the server refuses a priced
               block of any other kind by name — and it must travel, because a
               PUT without it clears what is stored. */
            ...(block.kind === 'not-included'
              ? { priceText: block.priceText }
              : {}),
          },
        },
        { onSuccess: done },
      ),
    remove: (block) => remove.mutate(block.id),
  }
}
